import { useState } from "react";

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

export function HashtagEditor({ value, onChange, disabled }: Props) {
  const [input, setInput] = useState("");

  const addTag = (raw: string) => {
    const t = raw.trim().replace(/^#?/, "#");
    if (t.length < 2) return;
    // Dedupe on normalised form — #factcheck and #faktencheck are
    // semantic dupes; also catches case variants + trailing punctuation.
    const normalise = (s: string) =>
      s
        .trim()
        .toLowerCase()
        .replace(/^#/, "")
        .replace(/ä/g, "ae")
        .replace(/ö/g, "oe")
        .replace(/ü/g, "ue")
        .replace(/ß/g, "ss")
        .replace(/[^a-z0-9]/g, "")
        // Known semantic synonyms
        .replace(/^factcheck$/, "faktencheck")
        .replace(/^disinformation$/, "desinformation");
    const key = normalise(t);
    if (!key) return;
    if (value.some((existing) => normalise(existing) === key)) return;
    onChange([...value, t]);
    setInput("");
  };

  const remove = (tag: string) => onChange(value.filter((t) => t !== tag));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {value.map((tag) => (
          <span
            key={tag}
            className="group flex items-center gap-1 rounded-md bg-brand-beige-deep px-2.5 py-1 font-mono text-[11px] text-brand-text-dark"
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={() => remove(tag)}
                className="ml-0.5 rounded-full text-brand-text-dark/40 hover:text-red-600"
                aria-label={`Remove ${tag}`}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <input
          type="text"
          value={input}
          placeholder="add hashtag + enter"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              if (input.trim()) addTag(input);
            } else if (e.key === "Backspace" && input === "" && value.length) {
              remove(value[value.length - 1]);
            }
          }}
          className="w-full rounded-lg border border-black/10 bg-white px-3 py-1.5 text-sm focus:border-brand-blue focus:outline-none"
        />
      )}
    </div>
  );
}
